import React, { useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom'; // Import useNavigate for redirection
import Button from './Button'; // Import custom Button if used for submit

interface IFormInput {
  name: string;
  email: string;
  company?: string;
  message: string;
}

interface GetStartedFormProps {
  onSuccess: () => void; // Used to close the modal
}

type WorkflowStep = 'initial' | 'contactForm' | 'success';

const GetStartedForm: React.FC<GetStartedFormProps> = ({ onSuccess }) => {
  const { register, handleSubmit, reset, formState: { errors } } = useForm<IFormInput>();
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [step, setStep] = useState<WorkflowStep>('initial');
  const navigate = useNavigate(); // Hook for navigation

  // Handler for initial choices
  const handleInitialChoice = (choice: 'basics' | 'solutions' | 'custom') => {
    if (choice === 'basics') {
      navigate('/ai-basics');
      onSuccess(); // Close modal
    } else if (choice === 'solutions') {
      navigate('/solutions');
      onSuccess(); // Close modal
    } else { // 'custom' or general contact
      setStep('contactForm');
    }
  };

  // Handler for form submission
  const onSubmit: SubmitHandler<IFormInput> = async (data) => {
    setIsLoading(true);
    setSubmitError(null);
    console.log("Modal Form Data:", data); // Log form data

    try {
      const response = await fetch('/api/enquiries', { // Using same endpoint as ContactPage
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        // Send required data, structure might differ from ContactPage
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          company: data.company || '',
          message: data.message,
          interest: 'Get Started Modal', // Indicate source
        }),
      });

      if (response.ok) {
        setStep('success');
        reset(); // Clear form fields
      } else {
        let errorMessage = 'Submission failed. Please try again.';
        try {
          const result = await response.json();
          errorMessage = result.message || errorMessage;
        } catch (parseError) {
          console.error("Could not parse error response:", parseError);
        }
        setSubmitError(errorMessage);
      }
    } catch (error) {
      console.error("Form submission network error:", error);
      setSubmitError('A network error occurred. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  // Initial Step Render
  const renderInitialStep = () => (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground dark:text-dark-muted-foreground">
        How would you like to begin?
      </p>
      <button 
        onClick={() => handleInitialChoice('basics')}
        className="w-full text-left p-3 border border-border dark:border-dark-border rounded-md hover:bg-secondary dark:hover:bg-dark-secondary transition duration-150"
      >
        <span className="font-medium text-foreground dark:text-dark-foreground">Learn AI Basics</span>
        <p className="text-xs text-muted-foreground dark:text-dark-muted-foreground">Explore fundamental AI concepts.</p>
      </button>
      <button 
        onClick={() => handleInitialChoice('solutions')}
        className="w-full text-left p-3 border border-border dark:border-dark-border rounded-md hover:bg-secondary dark:hover:bg-dark-secondary transition duration-150"
      >
        <span className="font-medium text-foreground dark:text-dark-foreground">Explore AI Solutions</span>
        <p className="text-xs text-muted-foreground dark:text-dark-muted-foreground">Browse our catalogue of AI tools.</p>
      </button>
      <button 
        onClick={() => handleInitialChoice('custom')}
        className="w-full text-left p-3 border border-border dark:border-dark-border rounded-md hover:bg-secondary dark:hover:bg-dark-secondary transition duration-150"
      >
        <span className="font-medium text-foreground dark:text-dark-foreground">Request Custom Solution / Contact Us</span>
        <p className="text-xs text-muted-foreground dark:text-dark-muted-foreground">Fill out a form for specific needs or general questions.</p>
      </button>
    </div>
  );

  // Contact Form Step Render
  const renderContactForm = () => (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <p className="text-sm text-muted-foreground dark:text-dark-muted-foreground mb-4">
          Tell us a bit about your project or challenge, and we'll get back to you soon.
      </p>
      {/* Name Input */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-foreground dark:text-dark-foreground">Full Name</label>
        <input 
          type="text" 
          id="name" 
          {...register("name", { required: "Name is required." })} 
          className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 sm:text-sm bg-input dark:bg-dark-input border-border dark:border-dark-border text-foreground dark:text-dark-foreground focus:ring-ring dark:focus:ring-dark-ring focus:border-ring dark:focus:border-dark-ring ${errors.name ? 'border-destructive dark:border-dark-destructive' : ''}`}
        />
        {errors.name && <p className="mt-1 text-sm text-destructive dark:text-dark-destructive">{errors.name.message}</p>}
      </div>
      {/* Email Input */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-foreground dark:text-dark-foreground">Email Address</label>
        <input 
          type="email" 
          id="email" 
          {...register("email", { required: "Email is required.", pattern: { value: /\S+@\S+\.\S+/, message: "Invalid email address." } })} 
          className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 sm:text-sm bg-input dark:bg-dark-input border-border dark:border-dark-border text-foreground dark:text-dark-foreground focus:ring-ring dark:focus:ring-dark-ring focus:border-ring dark:focus:border-dark-ring ${errors.email ? 'border-destructive dark:border-dark-destructive' : ''}`}
        />
        {errors.email && <p className="mt-1 text-sm text-destructive dark:text-dark-destructive">{errors.email.message}</p>}
      </div>
      {/* Company Input */}
      <div>
        <label htmlFor="company" className="block text-sm font-medium text-foreground dark:text-dark-foreground">Company (Optional)</label>
        <input 
          type="text" 
          id="company" 
          {...register("company")} 
          className="mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 sm:text-sm bg-input dark:bg-dark-input border-border dark:border-dark-border text-foreground dark:text-dark-foreground focus:ring-ring dark:focus:ring-dark-ring focus:border-ring dark:focus:border-dark-ring"
        />
      </div>
      {/* Message Textarea */}
      <div>
        <label htmlFor="message" className="block text-sm font-medium text-foreground dark:text-dark-foreground">How can we help?</label>
        <textarea 
          id="message" 
          rows={4} 
          {...register("message", { required: "Please let us know how we can help." })} 
          className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 sm:text-sm bg-input dark:bg-dark-input border-border dark:border-dark-border text-foreground dark:text-dark-foreground focus:ring-ring dark:focus:ring-dark-ring focus:border-ring dark:focus:border-dark-ring ${errors.message ? 'border-destructive dark:border-dark-destructive' : ''}`}
        />
        {errors.message && <p className="mt-1 text-sm text-destructive dark:text-dark-destructive">{errors.message.message}</p>}
      </div>
      {/* Submit Error Display */}
      {submitError && (
        <div className="p-3 bg-destructive/10 border border-destructive/30 text-destructive dark:bg-dark-destructive/20 dark:border-dark-destructive/40 dark:text-dark-destructive-foreground rounded-md">
          {submitError}
        </div>
      )}
      {/* Submit Button - Using custom Button component */}
      <Button 
        type="submit" 
        variant="primary"
        disabled={isLoading}
        className="w-full"
      >
        {isLoading ? 'Sending...' : 'Submit Request'}
      </Button>
    </form>
  );
  
  // Success Step Render
  const renderSuccessStep = () => (
    <div className="text-center py-6">
      <svg className="mx-auto h-12 w-12 text-success dark:text-dark-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <h3 className="mt-2 text-lg font-semibold text-foreground dark:text-dark-foreground">Message Sent!</h3>
      <p className="mt-2 text-sm text-muted-foreground dark:text-dark-muted-foreground">
        Thank you for reaching out. We'll be in touch soon.
      </p>
      <div className="mt-6">
        <Button 
          variant="secondary"
          onClick={onSuccess}
        >
          Close
        </Button>
      </div>
    </div>
  );
  
  // Main component render logic
  return (
    <div>
      {step === 'initial' && renderInitialStep()}
      {step === 'contactForm' && renderContactForm()}
      {step === 'success' && renderSuccessStep()}
    </div>
  );

};

export default GetStartedForm; 